// DashboardSettings.js
import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { doc, getDoc, updateDoc, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import CreditPurchaseModal from './CreditPurchaseModal';
import CreditService from '../services/creditService';
import './DashboardSettings.css';

function DashboardSettings({ userData }) {
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [notificationPreferences, setNotificationPreferences] = useState({
    creatorAccepts: true,
    contentSubmissions: true,
    revisionRequests: true,
    newMessages: true,
    projectDeadlines: true,
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [brandName, setBrandName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [showCreditPurchaseModal, setShowCreditPurchaseModal] = useState(false);
  const [currentCredits, setCurrentCredits] = useState(userData?.credits || 0);
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  useEffect(() => {
    if (userData?.profileImage) {
      setProfileImageUrl(userData.profileImage);
    }
    if (userData?.brandName) {
      setBrandName(userData.brandName);
    }
    if (userData?.emailNotifications !== undefined) {
      setEmailNotifications(userData.emailNotifications);
    }
    if (userData?.notificationPreferences) {
      setNotificationPreferences(userData.notificationPreferences);
    }
    if (userData?.credits !== undefined) {
      setCurrentCredits(userData.credits);
    }
  }, [userData]);

  // Load transaction history
  useEffect(() => {
    const loadTransactions = async () => {
      try {
        setLoadingTransactions(true);
        const transactionHistory = await CreditService.getTransactionHistory(10);
        setTransactions(transactionHistory);
      } catch (error) {
        console.error('Error loading transactions:', error);
      } finally {
        setLoadingTransactions(false);
      }
    };
    loadTransactions();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }
      setProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImageUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    setSaveMessage('');
    
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('User not logged in');

      let imageUrl = profileImageUrl;

      // Upload new profile image if selected
      if (profileImage) {
        const imageRef = ref(storage, `profileImages/${user.uid}`);
        await uploadBytes(imageRef, profileImage);
        imageUrl = await getDownloadURL(imageRef);
      }

      // Update user document
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        profileImage: imageUrl,
        brandName: brandName.trim(),
        emailNotifications,
        notificationPreferences,
      });

      setSaveMessage('Settings saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
      setProfileImage(null); // Clear the file input
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveMessage('Error saving settings. Please try again.');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleNotificationToggle = (key) => {
    setNotificationPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // SVG logo components
  const TikTokLogo = () => (
    <svg width="28" height="28" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="48" height="48" rx="12" fill="#000"/>
      <path d="M32.5 18.5C34.5 18.5 36.5 17.5 36.5 17.5V22.5C36.5 22.5 34.5 22.5 32.5 21.5V29C32.5 34 28.5 36 24.5 36C20.5 36 16.5 34 16.5 29C16.5 24 20.5 22 24.5 22V26C22.5 26 20.5 27 20.5 29C20.5 31 22.5 32 24.5 32C26.5 32 28.5 31 28.5 29V12.5H32.5V18.5Z" fill="#fff"/>
      <circle cx="32.5" cy="15.5" r="2.5" fill="#25F4EE"/>
      <circle cx="32.5" cy="15.5" r="1.5" fill="#FE2C55"/>
    </svg>
  );
  const FacebookLogo = () => (
    <svg width="28" height="28" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="48" height="48" rx="12" fill="#1877F3"/>
      <path d="M32 24H27V36H21V24H18V19H21V16.5C21 13.5 22.5 12 25.5 12H32V17H28.5C27.5 17 27 17.5 27 18.5V19H32L31 24Z" fill="#fff"/>
    </svg>
  );
  const InstagramLogo = () => (
    <svg width="28" height="28" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="48" height="48" rx="12" fill="url(#ig-gradient)"/>
      <defs>
        <linearGradient id="ig-gradient" x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
          <stop stopColor="#f9ce34"/>
          <stop offset="0.5" stopColor="#ee2a7b"/>
          <stop offset="1" stopColor="#6228d7"/>
        </linearGradient>
      </defs>
      <rect x="14" y="14" width="20" height="20" rx="6" fill="#fff"/>
      <circle cx="24" cy="24" r="6" fill="url(#ig-gradient)"/>
      <circle cx="31" cy="17" r="2" fill="#fff"/>
    </svg>
  );

  // Platform logo map
  const platformLogos = {
    TikTok: <TikTokLogo />,
    Facebook: <FacebookLogo />,
    Instagram: <InstagramLogo />,
  };

  // Ad account connections (fetch from userData or default to false)
  const adConnections = [
    { 
      platform: 'TikTok', 
      connected: userData?.tiktokConnected || false, 
      username: userData?.tiktokUsername || null 
    },
    { 
      platform: 'Facebook', 
      connected: userData?.facebookConnected || false, 
      username: userData?.facebookUsername || null 
    },
    { 
      platform: 'Instagram', 
      connected: userData?.instagramConnected || false, 
      username: userData?.instagramUsername || null 
    },
  ];

  return (
    <div className="dashboard-settings">
      <h2 className="settings-title">Account Settings</h2>

      <div className="settings-grid">
        {/* Profile & Account */}
        <div className="settings-box profile-account">
          <h4>Profile & Account</h4>
          <p>Update your profile picture and account information.</p>
          
          <div className="profile-picture-section">
            <div className="profile-picture-preview">
              <img 
                src={profileImageUrl || '/images/profile-placeholder.svg'} 
                alt="Profile" 
                className="profile-preview-image"
              />
            </div>
            <label className="profile-upload-btn">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: 'none' }}
              />
              {profileImage ? 'Change Picture' : 'Upload Picture'}
            </label>
          </div>

          <div className="settings-form-field">
            <label className="settings-form-label">Brand Name</label>
            <input
              type="text"
              className="settings-form-input"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              placeholder="Enter brand name"
            />
          </div>
          <div className="settings-info-row">
            <span className="settings-info-label">Email</span>
            <span className="settings-info-value">{userData?.email || getAuth().currentUser?.email || 'N/A'}</span>
          </div>

          <button 
            className="save-settings-btn" 
            onClick={handleSaveProfile}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          {saveMessage && (
            <p className={`save-message ${saveMessage.includes('Error') ? 'error' : 'success'}`}>
              {saveMessage}
            </p>
          )}
        </div>

        {/* Connected Ad Accounts */}
        <div className="settings-box ad-accounts ad-accounts-disabled">
          <h4>Connected Ad Accounts</h4>
          <p>Link your ad accounts to track performance automatically.</p>
          <div className="ad-accounts-coming-soon-container">
            <div className="ad-accounts-coming-soon-badge">
              <span className="ad-accounts-coming-soon-icon">★</span>
              Coming Soon
              <span className="ad-accounts-coming-soon-icon">★</span>
            </div>
          </div>
        </div>

        {/* Notification Preferences */}
        <div className="settings-box notifications">
          <h4>Notification Preferences</h4>
          <p>Choose what notifications you want to receive.</p>
          
          <div className="notification-toggle-section">
            <div className="toggle-row">
              <div className="toggle-label-group">
                <span className="toggle-label-text">Email Notifications</span>
                <span className="toggle-label-desc">Receive notifications via email</span>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={emailNotifications}
                  onChange={() => setEmailNotifications(!emailNotifications)}
                />
                <span className="slider" />
              </label>
            </div>
          </div>

          <div className="notification-preferences-list">
            <div className="notification-preference-item">
              <div className="preference-info">
                <span className="preference-label">Creator Accepts Projects</span>
                <span className="preference-desc">When a creator accepts your brief</span>
              </div>
              <label className="toggle-switch small">
                <input
                  type="checkbox"
                  checked={notificationPreferences.creatorAccepts}
                  onChange={() => handleNotificationToggle('creatorAccepts')}
                />
                <span className="slider" />
              </label>
            </div>

            <div className="notification-preference-item">
              <div className="preference-info">
                <span className="preference-label">Content Submissions</span>
                <span className="preference-desc">When creators submit content</span>
              </div>
              <label className="toggle-switch small">
                <input
                  type="checkbox"
                  checked={notificationPreferences.contentSubmissions}
                  onChange={() => handleNotificationToggle('contentSubmissions')}
                />
                <span className="slider" />
              </label>
            </div>

            <div className="notification-preference-item">
              <div className="preference-info">
                <span className="preference-label">Revision Requests</span>
                <span className="preference-desc">When you request revisions</span>
              </div>
              <label className="toggle-switch small">
                <input
                  type="checkbox"
                  checked={notificationPreferences.revisionRequests}
                  onChange={() => handleNotificationToggle('revisionRequests')}
                />
                <span className="slider" />
              </label>
            </div>

            <div className="notification-preference-item">
              <div className="preference-info">
                <span className="preference-label">New Messages</span>
                <span className="preference-desc">When you receive messages</span>
              </div>
              <label className="toggle-switch small">
                <input
                  type="checkbox"
                  checked={notificationPreferences.newMessages}
                  onChange={() => handleNotificationToggle('newMessages')}
                />
                <span className="slider" />
              </label>
            </div>

            <div className="notification-preference-item">
              <div className="preference-info">
                <span className="preference-label">Project Deadlines</span>
                <span className="preference-desc">Reminders for upcoming deadlines</span>
              </div>
              <label className="toggle-switch small">
                <input
                  type="checkbox"
                  checked={notificationPreferences.projectDeadlines}
                  onChange={() => handleNotificationToggle('projectDeadlines')}
                />
                <span className="slider" />
              </label>
            </div>
          </div>
        </div>

        {/* Billing & Credits */}
        <div className="settings-box billing">
          <h4>Billing & Credits</h4>
          <p>Manage your credits and view transaction history.</p>
          
          <div className="billing-credits-section">
            <div className="billing-credits-display">
              <div className="billing-credits-label">Current Balance</div>
              <div className="billing-credits-amount">{currentCredits.toFixed(2)}</div>
            </div>
            <button 
              className="billing-add-credits-btn" 
              onClick={() => setShowCreditPurchaseModal(true)}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Add Credits
            </button>
          </div>

          <div className="billing-transactions-section">
            <h5 className="billing-transactions-title">Recent Transactions</h5>
            {loadingTransactions ? (
              <div className="billing-loading">Loading transactions...</div>
            ) : transactions.length === 0 ? (
              <div className="billing-no-transactions">No transactions yet</div>
            ) : (
              <div className="billing-transactions-list">
                {transactions.map((transaction) => (
                  <div key={transaction.id} className="billing-transaction-item">
                    <div className="billing-transaction-info">
                      <div className="billing-transaction-type">
                        {transaction.type === 'purchase' || transaction.type === 'addition' ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="transaction-icon-add">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                          </svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="transaction-icon-deduct">
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                          </svg>
                        )}
                        <span className="billing-transaction-desc">
                          {transaction.description || `${transaction.type === 'purchase' || transaction.type === 'addition' ? 'Credits added' : 'Credits deducted'}`}
                        </span>
                      </div>
                      <div className="billing-transaction-date">
                        {transaction.timestamp ? new Date(transaction.timestamp).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        }) : 'N/A'}
                      </div>
                    </div>
                    <div className={`billing-transaction-amount ${transaction.type === 'purchase' || transaction.type === 'addition' ? 'positive' : 'negative'}`}>
                      {transaction.type === 'purchase' || transaction.type === 'addition' ? '+' : '-'}
                      {Math.abs(transaction.amount).toFixed(2)} credits
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Privacy & Security */}
        <div className="settings-box privacy-security">
          <h4>Privacy & Security</h4>
          <p>Manage your account security settings.</p>
          <div className="security-options">
            <div className="security-option-item">
              <div className="security-option-info">
                <span className="security-option-label">Two-Factor Authentication</span>
                <span className="security-option-desc">Add an extra layer of security</span>
              </div>
              <button className="security-action-btn" disabled>Enable (coming soon)</button>
            </div>
            <div className="security-option-item">
              <div className="security-option-info">
                <span className="security-option-label">Active Sessions</span>
                <span className="security-option-desc">View and manage active sessions</span>
              </div>
              <button className="security-action-btn" disabled>View (coming soon)</button>
            </div>
          </div>
        </div>

        {/* Delete Account */}
        <div className="settings-box danger-zone">
          <h4>Delete Account</h4>
          <p>This will permanently remove your account and all data. This action cannot be undone.</p>
          <button className="delete-btn" onClick={() => setShowDeleteModal(true)}>
            Delete My Account
          </button>
        </div>
      </div>

      {/* Credit Purchase Modal */}
      <CreditPurchaseModal
        isOpen={showCreditPurchaseModal}
        onClose={() => setShowCreditPurchaseModal(false)}
        currentCredits={currentCredits}
        onPurchaseSuccess={async () => {
          setShowCreditPurchaseModal(false);
          // Refresh credits and transactions
          try {
            const credits = await CreditService.getBalance();
            setCurrentCredits(credits);
            const transactionHistory = await CreditService.getTransactionHistory(10);
            setTransactions(transactionHistory);
          } catch (error) {
            console.error('Error refreshing credits:', error);
          }
        }}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="delete-modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Are you sure?</h3>
            <p>This action cannot be undone. Please confirm to delete your account.</p>
            <div className="delete-actions">
              <button
                className="confirm-delete-btn"
                onClick={() => {
                  alert('Account deletion request submitted.');
                  setShowDeleteModal(false);
                }}
              >
                Yes, Delete
              </button>
              <button
                className="cancel-delete-btn"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardSettings;
